import { type NLoginType, NUser, useNostrLogin } from "@nostrify/react/login";
import { useNostr } from "@nostrify/react";
import { useCallback, useMemo } from "react";
import { useAuthor } from "./useAuthor";

/**
 * Central auth hook. Converts the active login into an `NUser` and
 * merges in author metadata. Ported from treasures.to.
 *
 * `user.pubkey` is the hex public key; derive npub on demand with
 * `nip19.npubEncode(user.pubkey)`.
 */
export function useCurrentUser() {
  const { nostr } = useNostr();
  const { logins } = useNostrLogin();

  const loginToUser = useCallback(
    (login: NLoginType): NUser => {
      switch (login.type) {
        case "nsec":
          return NUser.fromNsecLogin(login);
        case "bunker":
          return NUser.fromBunkerLogin(login, nostr);
        case "extension":
          return NUser.fromExtensionLogin(login);
        default:
          throw new Error(`Unsupported login type: ${login.type}`);
      }
    },
    [nostr],
  );

  const users = useMemo(() => {
    const out: NUser[] = [];
    for (const login of logins) {
      try {
        out.push(loginToUser(login));
      } catch (error) {
        console.warn("Failed to create user from login:", error);
      }
    }
    return out;
  }, [logins, loginToUser]);

  const user = users[0] as NUser | undefined;
  const author = useAuthor(user?.pubkey);

  return {
    user,
    users,
    metadata: author.data?.metadata,
  };
}
